import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { testIncidentEmailMutation } from "@/api/queries";
import { useAuth } from "@/auth/auth-context";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";
import { useApiClient } from "@/lib/api-context";

/**
 * Super-admin probe for Resend + the incident HTML template.
 *
 * Does not open an incident. Independent of the incident channel, so
 * credentials can be checked while alerts still log to the console.
 */
export function TestIncidentEmailCard() {
  const { t } = useTranslation("pages");
  const { user } = useAuth();
  const client = useApiClient();
  const [to, setTo] = useState(user?.email ?? "");

  const mutation = useMutation({
    ...testIncidentEmailMutation(client),
  });

  const result = mutation.data;

  return (
    <Card className="mb-4">
      <CardBody>
        <div className="space-y-1">
          <h2 className="text-sm font-semibold text-ink">{t("testIncidentEmailTitle")}</h2>
          <p className="text-sm text-ink-muted">{t("testIncidentEmailDescription")}</p>
        </div>

        {/* Button sits beside the control, not the hint: items-end on a
            TextField with hint underneath parked the button on the hint
            baseline. Label + row + hint keeps them level. */}
        <div className="mt-3 flex flex-col gap-2">
          <label htmlFor="test-incident-email-to" className="text-sm font-medium text-ink">
            {t("testIncidentEmailTo")}
          </label>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <input
              id="test-incident-email-to"
              type="email"
              autoComplete="email"
              value={to}
              onChange={(event) => setTo(event.target.value)}
              aria-describedby="test-incident-email-to-hint"
              className="w-full min-w-0 flex-1 rounded-md border border-border-subtle bg-surface px-3.5 py-2.5 text-sm text-ink placeholder:text-ink-muted/70 focus-visible:border-brand focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand"
            />
            <Button
              className="shrink-0 sm:self-auto"
              loading={mutation.isPending}
              disabled={!to.trim() || mutation.isPending}
              onClick={() => mutation.mutate({ to: to.trim() })}
            >
              {t("testIncidentEmailSend")}
            </Button>
          </div>
          <p id="test-incident-email-to-hint" className="text-xs text-ink-muted">
            {t("testIncidentEmailToHint")}
          </p>
        </div>

        {mutation.isError ? (
          <Alert className="mt-3" tone="danger">
            {mutation.error.message}
          </Alert>
        ) : null}

        {result?.delivered === false ? (
          <Alert className="mt-3" tone="danger" title={t("testIncidentEmailFailedTitle")}>
            {result.error ?? t("testIncidentEmailFailedFallback")}
          </Alert>
        ) : null}

        {result?.delivered ? (
          <Alert className="mt-3" tone="info" title={t("testIncidentEmailSentTitle")} announce>
            {result.redirectedTo
              ? t("testIncidentEmailSentRedirected", {
                  to: result.to,
                  redirectedTo: result.redirectedTo,
                  subject: result.subject,
                })
              : t("testIncidentEmailSent", { to: result.to, subject: result.subject })}
          </Alert>
        ) : null}
      </CardBody>
    </Card>
  );
}
